// src/components/Admin/InvestigationStatusCard.tsx
import React from 'react';
import { CheckCircle, XCircle, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface InvestigationStatusCardProps {
  total: number;
  byStatus: Record<'under_review' | 'rejected' | 'deleted_by_user' | 'accepted', number>;
  onSelect: (s: 'accepted' | 'under_review' | 'rejected' | 'deleted_by_user') => void;
}

const STATUS_CFG = {
  accepted: {
    icon: CheckCircle,
    labelKey: 'accepted',
    color: 'text-green-600',
    border: 'border-2 border-green-600',
    bg: 'bg-green-50',
    hoverBg: 'hover:bg-green-100',
    hoverBorder: 'hover:border-green-700',
  },
  under_review: {
    icon: Loader2,
    labelKey: 'under_review',
    color: 'text-yellow-600',
    border: 'border-2 border-yellow-600',
    bg: 'bg-yellow-50',
    hoverBg: 'hover:bg-yellow-100',
    hoverBorder: 'hover:border-yellow-700',
  },
  rejected: {
    icon: XCircle,
    labelKey: 'rejected',
    color: 'text-red-600',
    border: 'border-2 border-red-600',
    bg: 'bg-red-50',
    hoverBg: 'hover:bg-red-100',
    hoverBorder: 'hover:border-red-700',
  },
  deleted_by_user: {
    icon: EyeOff,
    labelKey: 'deleted_by_user',
    color: 'text-purple-600',
    border: 'border-2 border-purple-600',
    bg: 'bg-purple-50',
    hoverBg: 'hover:bg-purple-100',
    hoverBorder: 'hover:border-purple-700',
  },
} as const;

const InvestigationStatusCard: React.FC<InvestigationStatusCardProps> = ({
  total,
  byStatus,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex-1 bg-slate-50/70 backdrop-blur-sm border-2 border-slate-300 ring-1 ring-slate-200 p-6 rounded-xl shadow-xl">
      {/* Cabecera */}
      <div className="flex flex-col items-center mb-6">
        <h2 className="font-semibold text-base text-slate-700">
          {t('admin.investigations')}
        </h2>
        <p className="text-5xl font-extrabold text-slate-800">{total}</p>
      </div>

      {/* Grid de estados */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        {(Object.keys(STATUS_CFG) as Array<keyof typeof STATUS_CFG>).map((status) => {
          const { icon: Icon, labelKey, color, border, bg, hoverBg, hoverBorder } = STATUS_CFG[status];
          const count = byStatus[status] ?? 0;

          return (
            <button
              key={status}
              className={`flex flex-col items-center ${bg} ${border} rounded-lg p-4 shadow-md transition
                          ${hoverBg} ${hoverBorder}
                          focus:outline-none focus:ring-2 focus:ring-offset-1 ${border.replace('border-', 'ring-')}`}
              onClick={() => onSelect(status)}
            >
              <Icon className={`h-6 w-6 ${color}`} />
              <span className="mt-1 text-slate-700">{t(`admin.status.${labelKey}`)}</span>
              <span className={`font-semibold mt-1 ${color}`}>{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InvestigationStatusCard;
